import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Avatar,
  Badge,
  Checkbox,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { red } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import useToggle from '../../../../hooks/useToggle';
import UserContext from '../../../../shared-components/contexts/UserContext';
import DeleteConfirmation from '../../../../shared-components/DeleteConfirmation';
import AlertIcon from '../../../../shared-components/Icons/AlertIcon';
import CheckinIcon from '../../../../shared-components/Icons/CheckinIcon';
import DataInsightIcon from '../../../../shared-components/Icons/DataInsightIcon';
import EncouragementIcon from '../../../../shared-components/Icons/EncouragementIcon';
import GroupMessageIcon from '../../../../shared-components/Icons/GroupMessageIcon';
import UpdateIcon from '../../../../shared-components/Icons/UpdateIcon';
import ExpandedNotation from '../../../../shared-components/Notation/ExpandedNotation';
import { INotation } from '../../../../shared-interfaces/Notation/INotation';
import { GetCoreUserFormattedName } from '../../../../shared-interfaces/User/ICoreUser';
import AlertService from '../../../../shared-services/Notation/AlertService';
import DataInsightService from '../../../../shared-services/Notation/DataInsightService';
import EncouragementService from '../../../../shared-services/Notation/EncouragementService';
import UpdateNoteService from '../../../../shared-services/Notation/UpdateNoteService';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import { NotationManagementDispatch } from '../NotationManagement';
import { NotationManagementActionType } from '../types/NotationManagementActionType';
import LowerAlert from './LowerAlert';

interface INotationListItem {
  notation: INotation;
  isSelected: boolean;
  onDelete: () => void;
}

const useStyles = makeStyles(theme => ({
  avatar: {
    height: '60px',
    width: '60px',
  },
  row: {
    borderBottom: '1px solid #dcdcdc',
  },
  listItem: {
    alignItems: 'center',
    paddingLeft: '13px',
    marginTop: '8px',
  },
  listItemText: {
    margin: 'auto',
    [theme.breakpoints.only('xs')]: {
      maxWidth: '70%',
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: '100%',
    },
    paddingLeft: '24px',
  },
  listItemRight: {
    marginTop: '8px',
    marginRight: 0,
  },
  heading: {
    fontWeight: 600,
  },
  link: {
    textDecoration: 'none',
    color: '#06c',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 600,
  },
  detailsContainer: {
    '& p': {
      margin: '0',
    },
  },
}));

export const NotationListItemSkeleton = () => {
  const classes = useStyles();
  return (
    <ListItem
      aria-busy="true"
      className={classes.row}
      alignItems="center"
      ContainerComponent="div"
    >
      <ListItemIcon className={classes.listItem}>
        <Skeleton variant="rectangular" width={24} height={24} />
      </ListItemIcon>
      <ListItemAvatar>
        <Skeleton variant="circular" className={classes.avatar} />
      </ListItemAvatar>
      <ListItemText
        className={classes.listItemText}
        primaryTypographyProps={{ variant: 'h3' }}
        disableTypography
        primary={<Skeleton variant="text" width={200} />}
        secondary={
          <>
            <Skeleton variant="text" width={300} />
            <Skeleton variant="text" width={250} />
          </>
        }
      />
      <div className={classes.listItemRight}>
        <div style={{ fontWeight: 600 }}>
          <Skeleton variant="rectangular" width={10} height={30} />
        </div>
      </div>
    </ListItem>
  );
};

const NotationListItem = (props: INotationListItem) => {
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useContext(NotationManagementDispatch);
  const user = useContext(UserContext);

  const isLayoutMd = useMediaQuery(theme.breakpoints.down('lg'));

  const isXS = useMediaQuery(theme.breakpoints.only('xs'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [openExpandedNotationDialog, toggleExpandedNotationDialog] =
    useToggle();

  const [openLowerAlertDialog, toggleLowerAlertDialog] = useToggle();
  const [openDeleteAlertDialog, toggleDeleteAlertDialog] = useToggle();

  const handleViewNotationDetails = () => {
    try {
      navigate(
        `/management/notation/${props.notation.notationType.toLowerCase()}/${
          props.notation.id
        }/details/`,
        { state: { notation: props.notation } },
      );
    } catch {
      navigate(
        `/management/notation/${props.notation.notationType.toLowerCase()}/${
          props.notation.id
        }/details/`,
      );
    }
  };

  let notationIcon = <AlertIcon />;
  if (props.notation.notationType === 'Encouragement')
    notationIcon = <EncouragementIcon />;
  else if (props.notation.notationType === 'Update')
    notationIcon = <UpdateIcon />;
  else if (props.notation.notationType === 'DataInsight')
    notationIcon = <DataInsightIcon />;
  else if (props.notation.notationType === 'CheckIn')
    notationIcon = <CheckinIcon />;
  else if (props.notation.notationType === 'GroupMessage')
    notationIcon = <GroupMessageIcon />;
  const isOpenAlert =
    props.notation.notationType === 'Alert' &&
    props.notation.alertStatus === 'Open';
  const isDeletable =
    ['Alert', 'Update', 'Encouragement'].findIndex(
      x => x === props.notation.notationType,
    ) !== -1;

  const dropdown = (
    <span style={{ float: 'right' }}>
      <IconButton
        aria-label="more actions"
        aria-controls={`actions-menu-${props.notation.id}`}
        aria-haspopup="true"
        onClick={(e: React.MouseEvent<HTMLElement>) =>
          setAnchorEl(e.currentTarget)
        }
        component="span"
        size="large"
      >
        <MoreVertIcon
          style={{
            width: isLayoutMd ? '50px' : undefined,
            height: isLayoutMd ? '50px' : undefined,
          }}
        />
      </IconButton>
      <Menu
        id={`actions-menu-${props.notation.id}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            handleViewNotationDetails();
            setAnchorEl(null);
          }}
        >
          View Notation Details
        </MenuItem>
        {isOpenAlert && (
          <MenuItem
            onClick={() => {
              toggleLowerAlertDialog();
              setAnchorEl(null);
            }}
          >
            Lower Alert
          </MenuItem>
        )}
        {user.isAdministrator && isDeletable && (
          <MenuItem
            onClick={() => {
              toggleDeleteAlertDialog();
              setAnchorEl(null);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </span>
  );

  const CateogryList = () => {
    const categoryNames = props.notation.categories.map(x => x.name);
    return (
      <>
        {categoryNames.length > 3 ? (
          <>
            {categoryNames.slice(0, 3).join(', ')}{' '}
            <Tooltip title={categoryNames.join(', ')} arrow>
              <em>+ {categoryNames.length - 3} more</em>
            </Tooltip>
          </>
        ) : (
          <>{categoryNames.join(', ')}</>
        )}
      </>
    );
  };

  return (
    <li
      className={classes.row}
      style={
        isOpenAlert
          ? {
              borderLeft: `${isXS ? '5px' : '8px'} solid ${red[500]}`,
            }
          : {
              borderLeft: `${isXS ? '5px' : '8px'} solid transparent`,
            }
      }
    >
      <ListItem
        ContainerComponent="div"
        aria-busy="true"
        alignItems="center"
        className={classes.listItem}
      >
        <ListItemIcon>
          <Checkbox
            inputProps={{ 'aria-label': 'select user' }}
            checked={props.isSelected}
            color="primary"
            onChange={event =>
              dispatch({
                type: NotationManagementActionType.NotationSelected,
                selected: event.target.checked,
                notation: props.notation,
              })
            }
          />
        </ListItemIcon>
        <ListItemAvatar>
          <Badge
            badgeContent={notationIcon}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            <Avatar
              alt={GetCoreUserFormattedName(props.notation.student, false)}
              src={
                props.notation.student?.profileImageFilePath ??
                props.notation.student?.institutionProfileImageFilePath
              }
              className={classes.avatar}
              data-clarity-mask="true"
            />
          </Badge>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          className={classes.listItemText}
          primary={
            <Typography variant="h3" className={classes.name}>
              {props.notation.notationType === NotationType.Alert
                ? `${isOpenAlert ? 'Open' : 'Lowered'} Alert for `
                : props.notation.notationType === NotationType.Encouragement
                ? 'Encouragement for '
                : props.notation.notationType === NotationType.Update
                ? 'Update for '
                : props.notation.notationType === NotationType.DataInsight
                ? 'Data Insight for '
                : ''}
              <a
                href={`/beacon/students/advise/student/${props.notation.student?.externalId}`}
                target="_blank"
                className={classes.link}
                data-clarity-mask="true"
              >
                {GetCoreUserFormattedName(props.notation.student, false)}
              </a>
            </Typography>
          }
          secondary={
            <>
              <Typography variant="caption">
                by {GetCoreUserFormattedName(props.notation.createdBy, false)} -{' '}
                {props.notation.createdOn.toLocaleDateString()} at{' '}
                {props.notation.createdOn.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
              {props.notation.audienceType && (
                <Typography component="div" variant="caption">
                  <strong>Audience: </strong>
                  <span>{props.notation.audienceType}</span>
                </Typography>
              )}
              {props.notation.categories &&
                props.notation.categories.length > 0 && (
                  <Typography component="div" variant="caption">
                    <strong>Categories: </strong>
                    <span data-clarity-mask="true">
                      <CateogryList />
                    </span>
                  </Typography>
                )}
              {props.notation.assignedTo && (
                <Typography component="div" variant="caption">
                  <strong>Assigned To: </strong>
                  <span data-clarity-mask="true">
                    {GetCoreUserFormattedName(props.notation.assignedTo, false)}
                  </span>
                </Typography>
              )}
            </>
          }
        />
        <ListItemSecondaryAction className={classes.listItemRight}>
          {dropdown}
        </ListItemSecondaryAction>
      </ListItem>
      <div style={{ padding: '0 40px 16px 152px' }}>
        <Typography
          variant="body1"
          className={classes.detailsContainer}
          data-clarity-mask="true"
          dangerouslySetInnerHTML={{
            __html:
              props.notation.details !== null
                ? props.notation.details.length > 300
                  ? props.notation.details
                      .substr(
                        0,
                        props.notation.details.substr(0, 300).lastIndexOf(' '),
                      )
                      .concat('…')
                  : props.notation.details
                : '',
          }}
        />
      </div>
      <ExpandedNotation
        open={openExpandedNotationDialog}
        close={toggleExpandedNotationDialog}
        onResponseAdded={(action, details, assignedTo, methodOfContact) => {
          if (props.notation.notationType === 'Alert') {
            dispatch({
              type: NotationManagementActionType.AssignTo,
              id: props.notation.id,
              assignTo: assignedTo,
            });
          }
          return Promise.resolve(true);
        }}
        notation={props.notation}
      />
      <LowerAlert
        open={openLowerAlertDialog}
        close={toggleLowerAlertDialog}
        id={props.notation.id}
        for={props.notation.student}
        assignedTo={props.notation.assignedTo}
        audience={props.notation.audienceType}
      />
      <DeleteConfirmation
        open={openDeleteAlertDialog}
        title="Are you sure you want to delete this notation?"
        close={toggleDeleteAlertDialog}
        onDelete={() => {
          (async () => {
            try {
              switch (props.notation.notationType) {
                case 'Update':
                  await UpdateNoteService.delete(props.notation.id);
                  break;
                case 'Alert':
                  await AlertService.delete(props.notation.id);
                  break;
                case 'Encouragement':
                  await EncouragementService.delete(props.notation.id);
                  break;
                case 'DataInsight':
                  await DataInsightService.delete(props.notation.id);
              }
              dispatch({
                type: NotationManagementActionType.DeleteNotation,
                notationType: props.notation.notationType,
                id: props.notation.id,
              });
              dispatch({ type: NotationManagementActionType.CloseModal });
              enqueueSnackbar('Deleted Notation', { variant: 'success' });
            } catch {
              enqueueSnackbar('Notation Deletion Failed', {
                variant: 'error',
              });
            }
          })();
        }}
      />
    </li>
  );
};

export default NotationListItem;
